/* eslint-disable max-len */
import {
    Box,
    Button,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { useMask } from '@react-input/mask';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { isPossiblePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { boolean, object, string } from 'yup';

import { getSelectedProperty } from 'entities/Property/model/selectors/PropertySelectors';
import { updateDeliveryInstructions } from 'entities/Property/model/services/updateDeliveryInstructions/updateDeliveryInstructions';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import FormikInput from 'shared/ui/Formik/FormikInput/FormikInput';
import { Loader } from 'shared/ui/Loader/Loader';

export const NAME_MAX_LENGTH = 200;
export const INSTRUCTIONS_MAX_LENGTH = 500;

const deliveryValidationSchema = object({
    name: string()
        .max(NAME_MAX_LENGTH, `Must be ${NAME_MAX_LENGTH} characters or less`)
        .required('Contact name is required'),
    usePropertyContact: boolean(),
    phone: string().when('usePropertyContact', {
        is: false,
        then: (schema) =>
            schema.test(
                'phoneValidity',
                'Phone number is required and must be valid',
                (value) => {
                    return (
                        !!value &&
                        isPossiblePhoneNumber(value, 'US') &&
                        isValidPhoneNumber(value, 'US')
                    );
                },
            ),
        otherwise: (schema) => schema.notRequired(),
    }),
    instructions: string()
        .max(
            INSTRUCTIONS_MAX_LENGTH,
            `Must be ${INSTRUCTIONS_MAX_LENGTH} characters or less`,
        )
        .notRequired(),
    email: string()
        .nullable()
        .notRequired()
        .email('Please enter a valid email address')
        // Validates email format: email@domain.extension
        .matches(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            'Please enter a valid email address',
        ),
});

interface SettingsOrdersDeliveryProps {
    name?: string;
    phone?: string;
    email?: string;
    usePropertyContact?: boolean;
    instructions?: string;
}

const SettingsOrdersDelivery = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const selectedProperty = useSelector(getSelectedProperty);

    const [loading, setLoading] = useState(false);
    const [submitClicked, setSubmitClicked] = useState(false);

    // Check if phone matches property phone (or is null/empty)
    const phoneMatchesProperty =
        !selectedProperty?.delRecipientPhone ||
        selectedProperty?.delRecipientPhone.replace(/\D/g, '') ===
            selectedProperty?.propertyPhoneNumber.toString().replace(/\D/g, '');

    // Check if email matches property email (or is null/empty)
    const emailMatchesProperty =
        selectedProperty?.delRecipientEmail === selectedProperty?.propertyEmail;

    const usePropertyContact = phoneMatchesProperty && emailMatchesProperty;

    // Only show propertyEmail when using property contact; otherwise show the saved recipient email (or empty)
    const displayEmail = usePropertyContact
        ? selectedProperty?.propertyEmail || ''
        : selectedProperty?.delRecipientEmail || '';

    const data = {
        usePropertyContact: usePropertyContact,
        name: selectedProperty?.delRecipientName,
        phone: selectedProperty?.delRecipientPhone,
        email: displayEmail,
        instructions: selectedProperty?.delInstructions || '',
    };

    const inputPhoneRef = useMask({
        mask: '(___) ___-____',
        replacement: { _: /\d/ },
    });

    const handleSubmit = async (
        values: SettingsOrdersDeliveryProps,
        helpers: FormikHelpers<SettingsOrdersDeliveryProps>,
    ): Promise<void> => {
        setLoading(true);
        const response = await dispatch(
            updateDeliveryInstructions({
                propertyId: selectedProperty.id,
                instructions: {
                    delInstructions: values.instructions,
                    delRecipientPhone: values.usePropertyContact
                        ? null
                        : values.phone,
                    delRecipientEmail: values.usePropertyContact
                        ? null
                        : values.email,
                    delRecipientName: values.name,
                },
            }),
        );

        if (response.meta.requestStatus !== 'fulfilled') {
            toast(t('Unknown Error'));
        } else {
            toast(t('Changes saved successfully'));
            helpers.resetForm({ values });
        }

        setLoading(false);
        setSubmitClicked(false);
    };

    return (
        <Box>
            <Typography typography="poppins.h2" marginBottom="20px">
                {t(`Delivery instructions`)}
            </Typography>

            <Box data-testid="page-description" mb="40px">
                <Typography mt="10px" typography="openSans.body2">
                    {t('Delivery instructions info')}
                </Typography>
            </Box>

            <Formik
                initialValues={data}
                validationSchema={deliveryValidationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
                validateOnBlur={false}
                validateOnChange={true}
            >
                {({
                    values,
                    errors,
                    handleSubmit,
                    setFieldValue,
                    handleChange,
                    resetForm,
                    validateForm,
                    isValid,
                    dirty,
                }) => {
                    const handleUsePropertyContactChange = async (
                        event: ChangeEvent<HTMLInputElement>,
                    ) => {
                        const checked = event.target.checked;

                        handleChange(event);

                        if (checked) {
                            await setFieldValue(
                                'phone',
                                selectedProperty.propertyPhoneNumber,
                            );
                            await setFieldValue(
                                'email',
                                selectedProperty.propertyEmail,
                            );
                        } else {
                            const phoneInput =
                                document.querySelector<HTMLInputElement>(
                                    '#phone',
                                );
                            phoneInput?.focus();
                        }

                        await validateForm();
                    };

                    return (
                        <Form onSubmit={handleSubmit}>
                            <Box maxWidth="660px">
                                <Box mb="30px" width="360px">
                                    <FormikInput
                                        name="name"
                                        label={t('Contact')}
                                        placeholder={t('Contact')}
                                        loading={loading}
                                        maxLength={NAME_MAX_LENGTH}
                                        validateOnTouch={false}
                                        hasError={
                                            submitClicked &&
                                            Boolean(errors?.name)
                                        }
                                    />
                                </Box>

                                <Box
                                    display="flex"
                                    flex="1"
                                    gap="24px"
                                    mb="30px"
                                    width="100%"
                                    alignItems="flex-start"
                                >
                                    <Box width="360px">
                                        <FormikInput
                                            name="phone"
                                            label={t('Phone')}
                                            placeholder={'(000) 000-0000'}
                                            loading={loading}
                                            disabled={values.usePropertyContact}
                                            maskRef={inputPhoneRef}
                                            maxLength={14}
                                            validateOnTouch={false}
                                            hasError={
                                                submitClicked &&
                                                Boolean(errors?.phone)
                                            }
                                        />
                                    </Box>
                                    <FormControlLabel
                                        control={
                                            <Field
                                                as={Switch}
                                                name="usePropertyContact"
                                                type="checkbox"
                                                disabled={loading}
                                                onChange={
                                                    handleUsePropertyContactChange
                                                }
                                            />
                                        }
                                        label={t('Use property contact info')}
                                        sx={{
                                            marginTop: '9px',
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: '15px',
                                            },
                                        }}
                                    />
                                </Box>

                                <Box mb="30px" width="360px">
                                    <FormikInput
                                        name="email"
                                        label={t('Email')}
                                        placeholder={t('Email')}
                                        loading={loading}
                                        disabled={values.usePropertyContact}
                                        validateOnTouch={false}
                                        hasError={
                                            submitClicked &&
                                            Boolean(errors?.email)
                                        }
                                    />
                                </Box>

                                <Field
                                    as={TextField}
                                    name="instructions"
                                    disabled={loading}
                                    placeholder={t(
                                        'Delivery instructions settings placeholder',
                                    )}
                                    label={t('Instructions')}
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        maxLength: INSTRUCTIONS_MAX_LENGTH,
                                    }}
                                />

                                <Box
                                    marginTop="20px"
                                    display={dirty ? 'flex' : 'none'}
                                    gap="20px"
                                    justifyContent="right"
                                >
                                    <Button
                                        color="primary"
                                        onClick={() => {
                                            resetForm();
                                            setSubmitClicked(false);
                                        }}
                                    >
                                        {t('Cancel')}
                                    </Button>
                                    <Button
                                        startIcon={
                                            loading && (
                                                <Loader color="inherit" />
                                            )
                                        }
                                        className={
                                            !isValid || !dirty || loading
                                                ? 'Mui-disabled'
                                                : ''
                                        }
                                        style={{
                                            pointerEvents: 'auto',
                                        }}
                                        variant="contained"
                                        type="submit"
                                        onClick={() => {
                                            setSubmitClicked(true);
                                        }}
                                    >
                                        {t(`Save`)}
                                    </Button>
                                </Box>
                            </Box>
                        </Form>
                    );
                }}
            </Formik>
        </Box>
    );
};

export default SettingsOrdersDelivery;
